import { useState } from 'preact/hooks';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { LoadedProps, RouteLoadProps, defRoute } from '@components/router';
import { useDocumentTitle } from 'client/utils/use-document-title';
import { CampaignPageShell } from '@course/components/campaign/campaign-page-shell';
import { CampaignHero } from '@course/components/campaign/campaign-hero';
import { useCurrentUser } from 'client/lib/auth';
import { showError } from '@components/app-error';
import { BtnPrimary } from '@components/buttons';
import { fmtFullTime } from 'shared/dateutil';
import { useTimezone } from 'client/lib/hooks';
import dayjs from 'dayjs';

export const route = defRoute({
  isPublic: true,
  load,
  Page,
});

async function load(route: RouteLoadProps) {
  return rpx.meetingRsvps.getJoinPage({
    meetingId: route.params.meetingId,
  });
}

type Props = LoadedProps<typeof load>;
type PageData = RpxResponse<typeof rpx.meetingRsvps.getJoinPage>;

function Page(props: Props) {
  const { meeting, course, status } = props.state;
  useDocumentTitle([meeting.title, course.title]);

  const subtitle =
    meeting.scheduledAt && status !== 'ended'
      ? fmtFullTime(meeting.scheduledAt, { includeTimezone: true })
      : undefined;

  return (
    <CampaignPageShell>
      <CampaignHero headline={meeting.title} subtitle={subtitle} />
      <main class="join-page">
        <BridgeCard {...props.state} />
      </main>
    </CampaignPageShell>
  );
}

async function recordAttendanceAndJoin(
  meetingId: string,
  opts?: { name?: string; email?: string; timezone?: string },
) {
  await rpx.meetingRsvps.rsvpToMeeting({ meetingId, ...opts });
  const result = await rpx.meetingRsvps.getJoinPage({ meetingId });
  if (result.joinUrl) {
    window.location.href = result.joinUrl;
  }
  return result.joinUrl;
}

function BridgeCard(props: PageData) {
  const { meeting, course, status, canJoin, timeLeft, joinUrl } = props;
  const user = useCurrentUser();
  const timezone = useTimezone();

  if (status === 'ended') {
    return <EndedState meeting={meeting} course={course} hasRecordings={props.hasRecordings} />;
  }

  if (status === 'draft' || !meeting.scheduledAt) {
    return (
      <div class="join-card">
        <h2 class="join-card__title">Session not yet scheduled</h2>
        <p class="join-card__body">Check back soon — the date and time hasn't been set yet.</p>
      </div>
    );
  }

  const isLive = status === 'inProgress' || (status === 'aboutToStart' && canJoin);

  if (!isLive) {
    return (
      <UpcomingState
        meeting={meeting}
        course={course}
        timezone={timezone}
        timeLeft={timeLeft}
        status={status}
        hasRsvped={props.hasRsvped}
      />
    );
  }

  if (user) {
    return <LoggedInBridge meeting={meeting} joinUrl={joinUrl} />;
  }

  return <GuestBridge meeting={meeting} />;
}

function LiveBadge() {
  return (
    <div class="join-live-badge" aria-label="Live now">
      <span class="join-live-badge__dot" aria-hidden="true" />
      LIVE NOW
    </div>
  );
}

function EndedState({
  meeting,
  course,
  hasRecordings,
}: {
  meeting: PageData['meeting'];
  course: PageData['course'];
  hasRecordings: boolean;
}) {
  const meetingUrl = `/courses/${course.id}/meetings/${meeting.id}`;

  return (
    <div class="join-card join-card--ended-state">
      <div class="join-card__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5" />
          <path
            d="M12 7v5l3 3"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
      {hasRecordings ? (
        <>
          <h2 class="join-card__title">This session has ended</h2>
          <p class="join-card__body">
            <strong>{meeting.title}</strong> with {course.title} has already taken place — but the
            recording is available for you to watch.
          </p>
          <a href={meetingUrl} class="join-card__cta join-card__cta--join">
            View the Recording
          </a>
        </>
      ) : (
        <>
          <h2 class="join-card__title">You just missed it!</h2>
          <p class="join-card__body">
            <strong>{meeting.title}</strong> with {course.title} has already taken place — but don't
            worry, we'd love to see you at the next one.
          </p>
          <p class="join-card__body">
            Have questions or want to make sure you don't miss the next session? Drop us a line:
          </p>
          <a href="mailto:aip@my.mirasee.com" class="join-card__email-link">
            aip@my.mirasee.com
          </a>
        </>
      )}
    </div>
  );
}

function UpcomingState({
  meeting,
  course,
  timezone,
  timeLeft,
  status,
  hasRsvped: initialHasRsvped,
}: {
  meeting: PageData['meeting'];
  course: PageData['course'];
  timezone: string;
  timeLeft: string;
  status: string;
  hasRsvped: boolean;
}) {
  const user = useCurrentUser();
  const [hasRsvped, setHasRsvped] = useState(initialHasRsvped);
  const [saving, setSaving] = useState(false);

  const formattedDate = fmtFullTime(meeting.scheduledAt, {
    timeZone: timezone,
    includeTimezone: true,
  });

  async function handleRsvp() {
    setSaving(true);
    try {
      if (hasRsvped) {
        await rpx.meetingRsvps.cancelRsvp({ meetingId: meeting.id });
        setHasRsvped(false);
      } else {
        await rpx.meetingRsvps.advanceRsvp({ meetingId: meeting.id });
        setHasRsvped(true);
      }
    } catch (err) {
      showError(err);
    } finally {
      setSaving(false);
    }
  }

  const showRsvp = meeting.rsvpEnabled && user;

  return (
    <div class="join-card">
      <div class="join-date-chip">
        <span class="join-date-chip__day">{dayjs(meeting.scheduledAt).format('dddd, MMMM D')}</span>
        <span class="join-date-chip__time">{dayjs(meeting.scheduledAt).format('h:mm A')}</span>
      </div>
      <h2 class="join-card__title">{meeting.title}</h2>
      <p class="join-card__course">{course.title}</p>
      {showRsvp ? (
        <div class="join-upcoming-message">
          {hasRsvped ? (
            <>
              <p style="display: flex; align-items: center; gap: 6px; color: #15803d; font-weight: 600;">
                <svg style="width: 18px; height: 18px;" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
                You're in! See you on {formattedDate}.
              </p>
              <p>
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleRsvp}
                  style="background: none; border: none; color: #9ca3af; font-size: 0.875rem; cursor: pointer; padding: 0; text-decoration: underline;"
                >
                  Cancel RSVP
                </button>
              </p>
            </>
          ) : (
            <>
              <p>This session is scheduled for {formattedDate}.</p>
              <BtnPrimary class="join-card__cta" isLoading={saving} onClick={handleRsvp}>
                Save my spot!
              </BtnPrimary>
            </>
          )}
        </div>
      ) : (
        <div class="join-upcoming-message">
          {status === 'aboutToStart' ? (
            <p>
              Starting in {timeLeft} — the join button will appear here when the session begins.
            </p>
          ) : (
            <>
              <p>This session is scheduled for {formattedDate}.</p>
              <p>
                Bookmark this page and come back when it's time — you'll be able to join right from
                here.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function LoggedInBridge({
  meeting,
  joinUrl,
}: {
  meeting: PageData['meeting'];
  joinUrl: string | undefined;
}) {
  const [loading, setLoading] = useState(false);
  const user = useCurrentUser()!;
  const firstName = (user.name || '').trim().split(/\s+/)[0];

  async function handleJoin() {
    if (joinUrl) {
      try {
        await rpx.meetingRsvps.rsvpToMeeting({ meetingId: meeting.id });
      } catch {
        // Non-fatal — attendance tracking shouldn't block joining
      }
      window.location.href = joinUrl;
      return;
    }
    setLoading(true);
    try {
      const url = await recordAttendanceAndJoin(meeting.id);
      if (!url) {
        showError(new Error('Join link is not available yet. Please try again in a moment.'));
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      showError(err);
    }
  }

  return (
    <div class="join-card join-card--live">
      <LiveBadge />
      <h2 class="join-card__title">{firstName ? `Welcome, ${firstName}!` : 'Welcome!'}</h2>
      <p class="join-card__body">
        <strong>{meeting.title}</strong> is live. Click below to join.
      </p>
      <BtnPrimary
        class="join-card__cta join-card__cta--join"
        isLoading={loading}
        onClick={handleJoin}
      >
        Join the session
      </BtnPrimary>
    </div>
  );
}

function GuestBridge({ meeting }: { meeting: PageData['meeting'] }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      return;
    }
    setLoading(true);
    try {
      const url = await recordAttendanceAndJoin(meeting.id, {
        name: name.trim(),
        email: email.trim(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      if (!url) {
        showError(new Error('Join link is not available yet. Please try again in a moment.'));
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      showError(err);
    }
  }

  return (
    <div class="join-card join-card--live">
      <LiveBadge />
      <h2 class="join-card__title">Join {meeting.title}</h2>
      <p class="join-card__body">Enter your details and we'll take you straight to the session.</p>
      <form class="join-form" onSubmit={handleSubmit}>
        <label class="join-form__field">
          <span class="join-form__label">Your name</span>
          <input
            type="text"
            class="join-form__input"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            required
            disabled={loading}
            autoComplete="name"
          />
        </label>
        <label class="join-form__field">
          <span class="join-form__label">Email address</span>
          <input
            type="email"
            class="join-form__input"
            value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            required
            disabled={loading}
            autoComplete="email"
          />
        </label>
        <BtnPrimary class="join-card__cta join-card__cta--join" isLoading={loading}>
          Join the session
        </BtnPrimary>
      </form>
    </div>
  );
}
